// frontend/src/utils/api.js
import axios from 'axios';
import { API_BASE } from '../config';

// Fine for the ordinary JSON call, far too short for anything that moves a file
// or waits on a model. Those call sites pass their own `timeout` — see
// UPLOAD_TIMEOUT below — rather than raising this one for everything, because a
// 60s hang on a plain GET reads as a dead app.
const DEFAULT_TIMEOUT = 15000;

// Photo uploads (a problem report is up to 4 × 5MB) and the vision model both
// routinely need more than 15s on the connection someone actually has standing
// at a street cabinet. Aborting them was not a safety measure: the request had
// usually landed, so every "retry" filed a second ticket.
export const UPLOAD_TIMEOUT = 120000;
export const AI_TIMEOUT = 90000;
// A card charge with no card form in sight. The window between "we gave up" and
// "the server committed" is where a customer gets charged twice, so this one is
// generous on purpose — see submitExtension in RentalDetail.
export const CHARGE_TIMEOUT = 60000;

const api = axios.create({
  baseURL: API_BASE,
  timeout: DEFAULT_TIMEOUT,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('glamgo_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Which language to answer in. The server writes the sentence that ends up in
  // the toast — errorText() prefers the server's words over the caller's
  // fallback, precisely so the specific reason survives — so without this every
  // refusal reaches a Greek customer in English. Read from i18next's own store
  // rather than from React state: this runs outside the component tree, and the
  // language the user is READING right now is the right one for a toast, which
  // is not necessarily the preference stored against their account.
  //
  // Read from the same key i18n.js initialises from ('glamgo-lang'), rather than
  // importing i18n here — this interceptor must not pull the whole i18n bundle
  // into every module that talks to the API.
  const lang = localStorage.getItem('glamgo-lang') || 'el';
  config.headers['Accept-Language'] = lang.slice(0, 2) === 'en' ? 'en' : 'el';
  return config;
});

// The ONLY 401s that mean "your session is over", and they all come from
// middleware/auth.js. Everything else is a route saying "those credentials are
// wrong": a mistyped current password on the profile screen, a wrong password
// on account deletion or on an email change, a bad login.
//
// This used to be the other way round — redirect on every 401 except an
// exempt-list of endpoints — and the same bug was then found three times, once
// per endpoint somebody added that re-checks a password. Wiping the token and
// navigating away destroys the toast before it renders, so the user is thrown
// out of the app with no explanation for something they could have fixed by
// retyping. Deciding from what the SERVER said, rather than from which URL was
// called, is what stops it coming back with the next such endpoint.
const SESSION_ENDED_ERRORS = new Set([
  'No token provided',
  'User not found',
  'This account has been deleted',
  'Session expired, please log in again',
  'Invalid token',
  // Suspended by an operator. Arrives as 403 rather than 401 — see below — but
  // it ends the session exactly as the others do, so it belongs in the same set
  // rather than in a second mechanism that would drift from this one.
  'ACCOUNT_LOCKED',
]);

// Escape hatch for a caller that handles its own 401 — pass
// `{ skipAuthRedirect: true }` in the request config. AuthContext's boot check
// uses it so that a stale token on a deliberately public page (/delete-account
// is required by Google Play to be readable while signed out) clears the
// session quietly instead of bouncing the reader to /login.
const isSessionExpiry = (err) => {
  if (err.config?.skipAuthRedirect) return false;
  const body = err.response?.data;
  const code = body && typeof body === 'object' ? body.error : null;
  return SESSION_ENDED_ERRORS.has(code);
};

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    // 403 is included for ACCOUNT_LOCKED specifically. The middleware answers a
    // suspension with 403 on purpose — 401 means "we do not know who you are",
    // and here we know exactly who they are and are refusing anyway — but the
    // consequence for the app is identical: the token is dead and holding it
    // just produces a screen full of failures with no explanation.
    if ((err.response?.status === 401 || err.response?.status === 403) && isSessionExpiry(err)) {
      localStorage.removeItem('glamgo_token');
      localStorage.removeItem('glamgo_user');
      window.location.href = '/login';
    }
    // Callers need the HTTP status, not just the message: a dispute appeal past
    // its deadline and a refund blocked by an open dispute both come back with a
    // server-worded `error`, and only the 404/409 tells the UI which story to
    // tell. The body is spread first so every existing `err.error` still works.
    const body = err.response?.data;
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      return Promise.reject({ ...body, status: err.response.status });
    }
    // No JSON body at all: a timeout, a dropped connection, an HTML error page.
    // `network: true` is the signal a caller needs to tell "the server refused"
    // apart from "we never heard back" — the second of which may well mean the
    // request landed and only the answer was lost.
    return Promise.reject(
      Object.assign(new Error(err.message || 'Network error'), {
        network: true,
        timeout: err.code === 'ECONNABORTED',
        status: err.response?.status,
        body,
      })
    );
  }
);

export default api;
